import Modal from "react-bootstrap/Modal";
import React, { useContext, useState } from "react";
import _ from "lodash";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { PrProfileContext } from "../../data/pr_profile_context_management";
import usePrProfileUpdateApi from "../../data/use_pr_profile_update_api";
import SaveSpinnerButton from "../../../profiles/SaveSpinnerButton";
import ServerErrorAlert from "../../../profiles/ServerErrorAlert";
import VisibilityControl from "../../../profiles/VisibilityControl";
import LocationField from "../../../profiles/LocationField";
import PrProfileSchema from "../../../../yup_schemas/pr_profile_schema";

const HEADER_ERROR_LABELS = {
  first_name: "First Name",
  last_name: "Last Name",
  headline_custom: "Headline",
  bio: "Bio",
  location_string: "Location",
  visibility: "Visibility",
};

/**
 * Canonicalizes a headline to the only two shapes the server should ever see: a trimmed nonblank
 * string, or null.
 *
 * The displayed headline is a coalesce over headline_custom, the AI headline and the job title, so an
 * empty string would mask every fallback and render a blank headline — clearing the field has to send
 * null, not "". Applying this to BOTH sides of the changed-fields diff is also what keeps a text input
 * on a profile that has no custom headline (raw null, rendered as an empty box) from looking like a
 * change and blanking the headline on every unrelated save.
 */
function canonicalizeHeadline(value) {
  const trimmed = typeof value === "string" ? value.trim() : value;
  return trimmed || null;
}

/**
 * Compares two objects and returns a new object with only the properties that
 * have changed.
 *
 * @param {Object} originalData - The original data object.
 * @param {Object} newData - The updated data object.
 * @return {Object} An object containing only the properties that have changed.
 *
 * @example
 * getChangedData({ a: 1, b: 3 }, { a: 1, b: 2 })
 *   => { b: 2 }
 * getChangedData({ a: 'foo', b: 'baz' }, { a: 'foo', c: 'bar' })
 *   => { c: 'bar' }
 * getChangedData({ x: 10, y: 20 }, { x: 10, y: 20 })
 *   => {}
 */
function getChangedData(originalData, newData) {
  return _.reduce(
    newData,
    (result, value, key) => (!_.isEqual(value, originalData[key]) ? { ...result, [key]: value } : result),
    {},
  );
}

// Header (name + bio) edit body. Reads the already-loaded profile from PrProfileContext and writes
// through use_pr_profile_update_api (mirrors Company's HeaderForm) — it no longer instantiates its
// own copy of the page state hook (the retired usePrProfileState), which used to trigger a second
// full top-level fetch on open.
function HeaderModalBody({ close }) {
  const context = useContext(PrProfileContext);
  const { prProfile, isUpdating, generalError } = context;
  const { updatePrProfile } = usePrProfileUpdateApi({ ...context });

  // Display state for the three-way control, seeded from the profile's current value. VisibilityControl
  // only fires onChange from an actual click (never on mount), so `setValue("visibility", ...)` below —
  // and therefore the changed-fields diff — only ever sees this key when the viewer actually touched it.
  const [visibility, setVisibility] = useState(prProfile.visibility);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(PrProfileSchema),
  });

  const onSubmit = async (data, event) => {
    event.preventDefault();
    const original = { ...prProfile, headline_custom: canonicalizeHeadline(prProfile.headline_custom) };
    const submitted = { ...data, headline_custom: canonicalizeHeadline(data.headline_custom) };
    // updatePrProfile closes the modal and silently reloads the page data on success.
    await updatePrProfile(getChangedData(original, submitted));
  };

  return (
    <>
      <Modal.Body>
        <ServerErrorAlert generalError={generalError} labelMap={HEADER_ERROR_LABELS} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-6">
              <div className="row">
                <label htmlFor="first_name" className="col-6 col-form-label">
                  First Name
                </label>
                <div className="col-6">
                  <input
                    id="first_name"
                    type="text"
                    className="form-control"
                    {...register("first_name")}
                    defaultValue={prProfile.first_name}
                  />
                  {errors?.first_name && <span className="small text-danger">{errors?.first_name?.message}</span>}
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="row">
                <label htmlFor="last_name" className="col-6 col-form-label">
                  Last Name
                </label>
                <div className="col-6">
                  <input
                    id="last_name"
                    type="text"
                    className="form-control"
                    {...register("last_name")}
                    defaultValue={prProfile.last_name}
                  />
                  {errors?.last_name && <span className="small text-danger">{errors?.last_name?.message}</span>}
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="row">
                <label htmlFor="headline_custom" className="col-12 col-form-label">
                  Headline
                </label>
                <div className="col-12">
                  <input
                    id="headline_custom"
                    type="text"
                    className="form-control"
                    {...register("headline_custom")}
                    defaultValue={prProfile.headline_custom}
                    // The derived headline showing today — so an owner with no custom headline can see
                    // what clearing this field falls back to.
                    placeholder={prProfile.headline}
                  />
                  {errors?.headline_custom && (
                    <span className="small text-danger">{errors?.headline_custom?.message}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="row">
                <label htmlFor="bio" className="col-12 col-form-label">
                  Bio
                </label>
                <div className="col-12">
                  <textarea id="bio" className="form-control" {...register("bio")} defaultValue={prProfile.bio} />
                  {errors?.bio && <span className="">{errors?.bio?.message}</span>}
                </div>
              </div>
            </div>
            <LocationField
              defaultValue={prProfile.location_string}
              setValue={setValue}
              error={errors?.location_string}
            />
            <div className="col-12">
              <div className="mt-3">
                <span className="col-form-label d-block">Profile Visibility</span>
                <VisibilityControl
                  value={visibility}
                  onChange={(newValue) => {
                    setVisibility(newValue);
                    // Registers the key with react-hook-form only on an actual click — see the comment
                    // by the `visibility` state above for why an untouched control must send no key.
                    setValue("visibility", newValue);
                  }}
                />
              </div>
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <SaveSpinnerButton
          inactiveText="Save Changes"
          activeText="Saving..."
          isSaving={isUpdating}
          className="btn btn-primary"
          onClick={handleSubmit(onSubmit)}
        />
        <button type="button" className="btn btn-secondary" onClick={close}>
          Close
        </button>
      </Modal.Footer>
    </>
  );
}

export default HeaderModalBody;
